import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Wallet, Plus, ArrowDownLeft, Ban, AlertOctagon, CheckCircle2, History,
  Paperclip, Trash2, Image as ImageIcon, Bell, Clock3,
} from "lucide-react";
import { toast } from "sonner";

// Phase 2H: the 6 visible lifecycle states (not_required hides the panel
// entirely, handled before this map is consulted).
const STATUS_PILL = {
  pending:            { c: "bg-slate-100 text-slate-700 border-slate-200", l: "Pending" },
  received:           { c: "bg-blue-50 text-blue-800 border-blue-200", l: "Received" },
  refund_pending:     { c: "bg-amber-50 text-amber-800 border-amber-200", l: "Refund pending" },
  refunded:           { c: "bg-emerald-50 text-emerald-800 border-emerald-200", l: "Refunded" },
  partially_withheld: { c: "bg-amber-50 text-amber-800 border-amber-200", l: "Partially withheld" },
  fully_withheld:     { c: "bg-red-50 text-red-800 border-red-200", l: "Fully withheld" },
};

const ACTION_LABEL = {
  collected: "Collected",
  refunded: "Refunded",
  withheld: "Withheld",
  requested: "Requested",
  refund_pending: "Refund marked pending",
  dispute_opened: "Dispute opened",
  dispute_resolved: "Dispute resolved",
};

// Mirrors backend bookings/deposit_service.py WITHHOLD_REASON_LABELS -
// keep in sync.
const WITHHOLD_REASONS = [
  { value: "damage", label: "Damage" },
  { value: "fuel_shortage", label: "Fuel/charge shortage" },
  { value: "late_return", label: "Late return" },
  { value: "cleaning", label: "Cleaning" },
  { value: "missing_item", label: "Missing item/key" },
  { value: "unpaid_balance", label: "Unpaid balance" },
  { value: "policy_violation", label: "Policy violation" },
  { value: "other", label: "Other" },
];
const WITHHOLD_REASON_LABELS = Object.fromEntries(WITHHOLD_REASONS.map((r) => [r.value, r.label]));

const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DepositPanel({ bookingId, onChanged }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(null); // 'collect' | 'refund' | 'withhold' | 'dispute' | 'resolve'
  const [busy, setBusy] = useState(false);
  const [proofs, setProofs] = useState([]);
  const [proofMeta, setProofMeta] = useState({ label: "receipt", linked_action: "", note: "" });
  const [proofBusy, setProofBusy] = useState(false);
  const [reviewFlag, setReviewFlag] = useState(null); // { incident_id, inspection_id, message }

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/deposit`);
      setState(data);
    } catch (e) {
      // not all users can see; keep silent
    }
  };

  const loadReviewFlag = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}`);
      const b = data?.booking || {};
      if (b.deposit_review_required) {
        setReviewFlag({
          incident_id: b.deposit_review_incident_id,
          inspection_id: b.deposit_review_inspection_id,
          reason: b.deposit_review_reason,
        });
      } else {
        setReviewFlag(null);
      }
    } catch (_) { /* hide on permission */ }
  };

   
  const loadProofs = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/deposit/proofs`);
      // hydrate each with data_url for inline display
      const withUrls = await Promise.all(
        (data.items || []).map(async (p) => {
          try {
            const { data: full } = await api.get(
              `/bookings/${bookingId}/deposit/proofs/${p.id}`);
            return full;
          } catch { return p; }
        })
      );
      setProofs(withUrls);
    } catch (_) { /* hide on no permission */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadProofs(); loadReviewFlag(); }, [bookingId]);

  if (!state || state.status === "not_required") return null;
  const pill = STATUS_PILL[state.status] || STATUS_PILL.pending;
  const dispute = state.dispute;
  const isDisputeOpen = dispute?.status === "open";

  const act = async (path, body, success) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/deposit/${path}`, body);
      setState(data);
      toast.success(success);
      setOpen(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="deposit-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#1B4332]" /> Deposit
        </h2>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${pill.c}`} data-testid="deposit-status-pill">
          {pill.l}
        </span>
      </div>

      {reviewFlag && (
        <div className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3"
             data-testid="deposit-review-banner">
          <div className="text-sm font-semibold text-amber-900 inline-flex items-center gap-1">
            <AlertOctagon className="w-4 h-4" /> Deposit review recommended
          </div>
          <p className="text-xs text-amber-900/80 mt-1">
            Return inspection found possible damage/missing items. Review the inspection and incident before refunding or withholding the deposit.
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {reviewFlag.inspection_id && (
              <a href={`/app/inspections/${reviewFlag.inspection_id}`}
                 data-testid="deposit-review-inspection-link"
                 className="px-2 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900">
                View return inspection
              </a>
            )}
            {reviewFlag.incident_id && (
              <a href={`/app/incidents/${reviewFlag.incident_id}`}
                 data-testid="deposit-review-incident-link"
                 className="px-2 py-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900">
                View incident
              </a>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <Stat label="Required" value={peso(state.required)} testId="deposit-required" />
        <Stat label="Collected" value={peso(state.collected)} testId="deposit-collected" />
        <Stat label="Refunded" value={peso(state.refunded)} testId="deposit-refunded" />
        <Stat label="Withheld" value={peso(state.withheld)} testId="deposit-withheld" />
      </div>

      <div className="text-xs text-slate-500 mt-2">
        Remaining held: <span className="font-semibold text-slate-700">{peso(state.remaining_held)}</span>
        {state.outstanding_to_collect > 0 && (
          <> · Still to collect: <span className="font-semibold text-amber-700">{peso(state.outstanding_to_collect)}</span></>
        )}
        {state.method && <> · Method: <span className="font-medium">{state.method}</span></>}
        {state.reference && <> · Ref: <span className="font-mono">{state.reference}</span></>}
      </div>

      {state.status === "pending" && (
        <div className="text-xs text-slate-500 mt-1" data-testid="deposit-requested-line">
          {state.requested_at
            ? <>Requested {new Date(state.requested_at).toLocaleString()}{state.requested_channel ? ` · via ${state.requested_channel}` : ""}</>
            : "Not yet requested"}
        </div>
      )}

      {isDisputeOpen && dispute && (
        <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900" data-testid="deposit-dispute-card">
          <div className="font-semibold inline-flex items-center gap-1"><AlertOctagon className="w-4 h-4" /> Dispute open</div>
          <div className="text-xs mt-1"><b>Reason:</b> {dispute.reason}</div>
          {dispute.note && <div className="text-xs"><b>Note:</b> {dispute.note}</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {state.status === "pending" && !state.requested_at && (
          <ActionBtn icon={Bell} label="Request deposit"
                     onClick={() => setOpen("request")}
                     testId="deposit-request-btn" />
        )}
        {state.outstanding_to_collect > 0 && (
          <ActionBtn icon={Plus} label="Collect"
                     onClick={() => setOpen("collect")}
                     testId="deposit-collect-btn" />
        )}
        {state.remaining_held > 0 && !isDisputeOpen && (
          <>
            <ActionBtn icon={ArrowDownLeft} label="Refund"
                       onClick={() => setOpen("refund")}
                       testId="deposit-refund-btn" />
            <ActionBtn icon={Ban} label="Withhold"
                       onClick={() => setOpen("withhold")}
                       testId="deposit-withhold-btn" />
            {state.status === "received" && (
              <ActionBtn icon={Clock3} label="Decide later"
                         onClick={() => act("mark-refund-pending", {}, "Marked as refund pending")}
                         testId="deposit-mark-refund-pending-btn" />
            )}
            <ActionBtn icon={AlertOctagon} label="Open dispute"
                       onClick={() => setOpen("dispute")}
                       testId="deposit-dispute-btn" />
          </>
        )}
        {isDisputeOpen && (
          <ActionBtn icon={CheckCircle2} label="Resolve dispute" primary
                     onClick={() => setOpen("resolve")}
                     testId="deposit-resolve-btn" />
        )}
      </div>

      {open === "request" && (
        <ActionForm
          title="Request deposit from customer"
          fields={[
            { key: "channel", label: "Channel (optional)", type: "select",
              options: ["", "email", "sms", "in_person", "other"],
              optionLabels: { "": "Not specified", email: "Email", sms: "SMS", in_person: "In person", other: "Other" } },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-request"
          onSubmit={(v) => act("request", v, "Deposit requested")}
        />
      )}

      {open === "collect" && (
        <ActionForm
          title="Collect deposit"
          fields={[
            { key: "amount", label: "Amount", type: "number", required: true, placeholder: state.outstanding_to_collect },
            { key: "method", label: "Method", type: "select", options: ["cash", "gcash", "bank_transfer", "card", "other"] },
            { key: "reference", label: "Reference (optional)" },
            { key: "note", label: "Note (optional)", textarea: true },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-collect"
          onSubmit={(v) => act("collect", v, "Deposit collected")}
        />
      )}
      {open === "refund" && (
        <ActionForm
          title="Refund deposit"
          fields={[
            { key: "amount", label: `Amount (max ${peso(state.remaining_held)})`, type: "number", required: true },
            { key: "method", label: "Method", type: "select", options: ["cash", "gcash", "bank_transfer", "card", "other"] },
            { key: "reference", label: "Reference (optional)" },
            { key: "note", label: "Note (optional)", textarea: true },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-refund"
          onSubmit={(v) => act("refund", v, "Refund recorded")}
        />
      )}
      {open === "withhold" && (
        <ActionForm
          title="Withhold deposit"
          danger
          fields={[
            { key: "amount", label: `Amount (max ${peso(state.remaining_held)})`, type: "number", required: true },
            { key: "reason", label: "Reason (visible in audit log)", type: "select",
              options: WITHHOLD_REASONS.map((r) => r.value), optionLabels: WITHHOLD_REASON_LABELS,
              required: true },
            { key: "note", label: "Note (required if reason is Other)", textarea: true },
            { key: "linked_incident_id", label: "Linked incident (optional)",
              default: reviewFlag?.incident_id || "", placeholder: "incident id" },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-withhold"
          onSubmit={(v) => act("withhold", v, "Deposit withheld")}
        />
      )}
      {open === "dispute" && (
        <ActionForm
          title="Open deposit dispute"
          fields={[
            { key: "reason", label: "Reason (required)", textarea: true, required: true },
            { key: "note", label: "Internal note (optional)", textarea: true },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-dispute"
          onSubmit={(v) => act("dispute", v, "Dispute opened")}
        />
      )}
      {open === "resolve" && (
        <ActionForm
          title="Resolve dispute"
          fields={[
            { key: "resolution", label: "Resolution", type: "select",
              options: ["refund", "withhold", "split", "other"], required: true },
            { key: "refund_amount", label: `Refund amount (max ${peso(state.remaining_held)})`, type: "number" },
            { key: "withhold_amount", label: `Withhold amount (max ${peso(state.remaining_held)})`, type: "number" },
            { key: "note", label: "Note (optional)", textarea: true },
          ]}
          busy={busy} onClose={() => setOpen(null)}
          testIdPrefix="deposit-resolve"
          onSubmit={(v) => act("resolve-dispute", v, "Dispute resolved")}
        />
      )}

      {/* Proof photos (receipts, screenshots, damage evidence) */}
      <DepositProofs
        bookingId={bookingId}
        proofs={proofs}
        meta={proofMeta}
        setMeta={setProofMeta}
        busy={proofBusy}
        setBusy={setProofBusy}
        onChanged={loadProofs}
      />

      {/* History */}
      <div className="mt-5">
        <h3 className="font-display text-sm font-semibold inline-flex items-center gap-1"><History className="w-3.5 h-3.5" /> History</h3>
        <ol className="mt-2 space-y-2 text-xs" data-testid="deposit-history">
          {state.history.length === 0 ? (
            <li className="text-slate-500 italic" data-testid="deposit-history-empty">No deposit activity yet.</li>
          ) : state.history.slice().reverse().map((h, i) => (
            <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">
                  {ACTION_LABEL[h.action] || h.action}
                  {h.amount !== undefined && ` · ${peso(h.amount)}`}
                </span>
                <span className="text-slate-500">{new Date(h.at).toLocaleString()} · {h.by_name || "-"}</span>
              </div>
              {h.method && <div className="text-slate-600">Method: {h.method}{h.reference ? ` · Ref ${h.reference}` : ""}</div>}
              {h.reason && (
                <div className="text-slate-700">
                  <b>Reason:</b> {WITHHOLD_REASON_LABELS[h.reason] || h.reason}
                  {h.linked_incident_id && <> · linked to incident {h.linked_incident_id.slice(0, 8)}</>}
                </div>
              )}
              {h.resolution && <div className="text-slate-700"><b>Resolution:</b> {h.resolution}{h.refund_amount !== undefined ? ` · refund ${peso(h.refund_amount)}` : ""}{h.withhold_amount !== undefined ? ` · withhold ${peso(h.withhold_amount)}` : ""}</div>}
              {h.previous_status && h.new_status && (
                <div className="text-slate-500">{h.previous_status.replace(/_/g, " ")} → {h.new_status.replace(/_/g, " ")}</div>
              )}
              {h.note && <div className="text-slate-700"><b>Note:</b> {h.note}</div>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900" data-testid={testId}>{value}</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testId, primary }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
              primary ? "bg-[#1B4332] text-white hover:opacity-90" : "bg-white border border-slate-300 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function ActionForm({ title, fields, onSubmit, onClose, busy, danger, testIdPrefix }) {
  const initial = {};
  fields.forEach((f) => {
    initial[f.key] = f.default !== undefined ? f.default
      : f.type === "select" ? (f.options?.[0] || "") : "";
  });
  const [values, setValues] = useState(initial);

  const submit = () => {
    for (const f of fields) {
      if (f.required && !String(values[f.key] ?? "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const payload = {};
    for (const [k, v] of Object.entries(values)) {
      if (v === "" || v === undefined || v === null) continue;
      payload[k] = (fields.find((x) => x.key === k)?.type === "number") ? Number(v) : v;
    }
    onSubmit(payload);
  };

  return (
    <div className={`mt-3 rounded-lg border p-3 ${danger ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`} data-testid={`${testIdPrefix}-form`}>
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="space-y-2">
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">{f.label}</span>
            {f.type === "select" ? (
              <select value={values[f.key]} onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                      data-testid={`${testIdPrefix}-${f.key}`}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                {f.options.map((o) => (
                  <option key={o} value={o}>{f.optionLabels?.[o] ?? (o ? o.replace("_", " ") : "-")}</option>
                ))}
              </select>
            ) : f.textarea ? (
              <textarea rows={2} value={values[f.key]}
                        placeholder={f.placeholder || ""}
                        data-testid={`${testIdPrefix}-${f.key}`}
                        onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            ) : (
              <input type={f.type || "text"} value={values[f.key]}
                     placeholder={f.placeholder || ""}
                     data-testid={`${testIdPrefix}-${f.key}`}
                     onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                     className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            )}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} data-testid={`${testIdPrefix}-cancel`}
                className="px-3 py-1.5 rounded-md text-sm hover:bg-slate-100">Cancel</button>
        <button onClick={submit} disabled={busy}
                data-testid={`${testIdPrefix}-submit`}
                className={`px-3 py-1.5 rounded-md text-sm text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-[#1B4332] hover:opacity-90"} disabled:opacity-50`}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function DepositProofs({ bookingId, proofs, meta, setMeta, busy, setBusy, onChanged }) {
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large: max 4 MB.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const base64 = dataUrl.split(",")[1];
      await api.post(`/bookings/${bookingId}/deposit/proofs`, {
        name: file.name, content_type: file.type || "image/jpeg",
        data_base64: base64,
        label: meta.label || null,
        linked_action: meta.linked_action || null,
        note: meta.note || null,
      });
      toast.success("Proof uploaded");
      setMeta({ label: "receipt", linked_action: "", note: "" });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (pid) => {
    try {
      await api.delete(`/bookings/${bookingId}/deposit/proofs/${pid}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mt-5" data-testid="deposit-proofs">
      <h3 className="font-display text-sm font-semibold inline-flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Proof photos & receipts</h3>
      <p className="text-xs text-slate-500 mt-1">Attach screenshots (GCash, bank transfer), receipts, or photos of damage as evidence for collect/refund/withhold actions.</p>

      <div className="mt-3 grid sm:grid-cols-3 gap-2">
        <select value={meta.label}
                onChange={(e) => setMeta((m) => ({ ...m, label: e.target.value }))}
                data-testid="deposit-proof-label"
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5">
          <option value="receipt">Receipt</option>
          <option value="gcash_screenshot">GCash screenshot</option>
          <option value="bank_transfer">Bank transfer</option>
          <option value="damage_photo">Damage photo</option>
          <option value="other">Other</option>
        </select>
        <select value={meta.linked_action}
                onChange={(e) => setMeta((m) => ({ ...m, linked_action: e.target.value }))}
                data-testid="deposit-proof-linked-action"
                className="text-sm rounded-md border border-slate-300 px-2 py-1.5">
          <option value="">(unlinked)</option>
          <option value="collected">Collected</option>
          <option value="refunded">Refunded</option>
          <option value="withheld">Withheld</option>
          <option value="dispute">Dispute evidence</option>
        </select>
        <input type="text" placeholder="Note (optional)"
               value={meta.note}
               onChange={(e) => setMeta((m) => ({ ...m, note: e.target.value }))}
               data-testid="deposit-proof-note"
               className="text-sm rounded-md border border-slate-300 px-2 py-1.5" />
      </div>

      <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm cursor-pointer"
             data-testid="deposit-proof-upload-btn">
        <Paperclip className="w-4 h-4" /> {busy ? "Uploading…" : "Upload photo"}
        <input type="file" accept="image/*" hidden onChange={onPick}
               data-testid="deposit-proof-file" />
      </label>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
           data-testid="deposit-proof-grid">
        {proofs.length === 0 ? (
          <div className="col-span-full text-xs italic text-slate-500" data-testid="deposit-proof-grid-empty">
            No proof photos yet.
          </div>
        ) : proofs.map((p) => (
            <div key={p.id} className="group relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                 data-testid={`deposit-proof-${p.id}`}>
              {p.data_url ? (
                <img src={p.data_url} alt={p.name} className="w-full h-28 object-cover" />
              ) : (
                <div className="w-full h-28 grid place-items-center bg-slate-100 text-slate-400">
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
              <div className="text-[10px] p-1.5 text-slate-600">
                <div className="font-medium">{p.label || "receipt"}</div>
                {p.linked_action && <div className="text-slate-500">→ {p.linked_action}</div>}
              </div>
              <button onClick={() => del(p.id)}
                      data-testid={`deposit-proof-delete-${p.id}`}
                      className="absolute top-1 right-1 p-1 rounded-md bg-white/90 hover:bg-white text-red-600 opacity-0 group-hover:opacity-100 transition">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}

